/**
 * kbUi.tsx — shared presentational bits for the Knowledge Base surfaces.
 * Ported from prototype stage-kb-ingest.jsx (status pills, domain chips/pills,
 * confidence bar, snippet panels, helpers). No state — pure UI.
 */
import { I, type IconComponent } from "../../components/icons";
import { kbStore } from "../../lib/kbStore";
import type { KbDecision, KbDocStatus } from "../../lib/mock";

export function kbRgba(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

export function kbConfTone(c: number): string {
  return c >= 0.8 ? "var(--c-ok)" : c >= 0.6 ? "var(--c-warn)" : "var(--c-err)";
}

export function clip(s: string | null, n: number): string {
  if (!s) return "";
  return s.length > n ? s.slice(0, n).trimEnd() + "…" : s;
}

export const REC_LABEL: Record<KbDecision, string> = {
  accept_new: "Accetta nuovo",
  reject_new: "Mantieni esistente",
  merge: "Unisci",
  clarification: "Chiedi chiarimento",
};

export const DECISION_PILL: Record<KbDecision, { cls: string; label: string; muted?: boolean }> = {
  accept_new: { cls: "pill-ok", label: "Accettato" },
  reject_new: { cls: "", label: "Rifiutato", muted: true },
  merge: { cls: "pill-info", label: "Unito" },
  clarification: { cls: "pill-warn", label: "Richiesto chiarimento" },
};

const KB_STATUS: Record<KbDocStatus, { cls: string; label: string }> = {
  live: { cls: "pill-ok", label: "Live" },
  "pending-review": { cls: "pill-warn", label: "In revisione" },
  rejected: { cls: "", label: "Rifiutato" },
  ingesting: { cls: "pill-info", label: "Ingest…" },
  archived: { cls: "", label: "Archiviato" },
  "pending-manual-merge": { cls: "pill-info", label: "Da unire" },
};

export function KBStatusPill({ status }: { status: KbDocStatus }): JSX.Element {
  const s = KB_STATUS[status] ?? KB_STATUS.live;
  if (status === "ingesting") {
    return (
      <span className="pill pill-info" style={{ gap: 5 }}>
        <span
          className="spinner"
          style={{
            width: 9,
            height: 9,
            borderTopColor: "var(--c-info)",
            borderColor: "rgba(37,99,168,0.25)",
          }}
        />
        {s.label}
      </span>
    );
  }
  if (status === "rejected" || status === "archived") {
    return (
      <span className="pill" style={{ opacity: 0.55 }}>
        {s.label}
      </span>
    );
  }
  return <span className={`pill ${s.cls}`}>{s.label}</span>;
}

export function DomainDot({ color, size = 8 }: { color: string; size?: number }): JSX.Element {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        display: "inline-block",
        flexShrink: 0,
      }}
    />
  );
}

export function DomainPill({ domainKey }: { domainKey: string }): JSX.Element {
  const d = kbStore.domain(domainKey);
  if (!d) return <span className="pill">{domainKey}</span>;
  return (
    <span
      className="pill"
      style={{
        background: kbRgba(d.color, 0.1),
        color: d.color,
        borderColor: kbRgba(d.color, 0.28),
      }}
    >
      <DomainDot color={d.color} size={7} />
      {d.label}
    </span>
  );
}

const CHIP = { brand: "#1E3A5F", bg: "#FAFAF8", ink: "#3D424E", line: "#D6D3CC" };

export function DomainChip({
  label,
  color,
  active,
  onClick,
}: {
  label: string;
  color?: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  const activeBg = color || CHIP.brand;
  return (
    <button
      onClick={onClick}
      style={{
        appearance: "none",
        cursor: "pointer",
        fontFamily: "inherit",
        height: 26,
        padding: "0 11px",
        borderRadius: 13,
        fontSize: 12,
        fontWeight: 500,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        backgroundColor: active ? activeBg : CHIP.bg,
        color: active ? "#fff" : CHIP.ink,
        border: `1px solid ${active ? activeBg : CHIP.line}`,
        transition:
          "background-color var(--tr-fast), border-color var(--tr-fast), color var(--tr-fast)",
      }}
    >
      {color && <DomainDot color={active ? "#fff" : color} size={7} />}
      {label}
    </button>
  );
}

export function ConfidenceBar({
  value,
  width = 160,
}: {
  value: number;
  width?: number;
}): JSX.Element {
  const pct = Math.round(value * 100);
  const tone = kbConfTone(value);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div
        style={{
          width,
          height: 6,
          borderRadius: 3,
          background: "var(--c-bg-sunken)",
          overflow: "hidden",
          border: "1px solid var(--c-line)",
        }}
      >
        <div style={{ width: `${pct}%`, height: "100%", background: tone }} />
      </div>
      <span className="t-mono t-num" style={{ fontSize: 12, fontWeight: 600, color: tone }}>
        {pct}%
      </span>
    </div>
  );
}

export function SnippetPanel({
  tone,
  eyebrow,
  docId,
  source,
  text,
}: {
  tone: "new" | "existing";
  eyebrow: string;
  docId: string | null;
  source: string | null;
  text: string | null;
}): JSX.Element {
  const accent = tone === "new" ? "var(--c-ai)" : "var(--c-ink-3)";
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        border: "1px solid var(--c-line)",
        borderRadius: 10,
        overflow: "hidden",
        background: "var(--c-bg)",
      }}
    >
      <div
        style={{
          padding: "8px 12px",
          borderBottom: "1px solid var(--c-line)",
          background: tone === "new" ? "var(--c-ai-soft)" : "var(--c-bg-sunken)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: accent,
          }}
        >
          {eyebrow}
        </span>
        {docId && (
          <span
            className="t-mono"
            style={{ fontSize: 10.5, color: "var(--c-ink-3)", marginLeft: "auto" }}
          >
            {docId}
          </span>
        )}
      </div>
      <div style={{ padding: "12px 14px" }}>
        {text ? (
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: "var(--c-ink)" }}>
            “{text}”
          </p>
        ) : (
          <p style={{ margin: 0, fontSize: 12.5, color: "var(--c-ink-3)", fontStyle: "italic" }}>
            Nessun documento esistente in conflitto — segnalazione per contenuto off-topic.
          </p>
        )}
        {source && (
          <div style={{ marginTop: 10, fontSize: 11, color: "var(--c-ink-3)" }}>
            <I.File
              size={10}
              style={{ display: "inline", verticalAlign: "-1px", marginRight: 4 }}
            />
            {source}
          </div>
        )}
      </div>
    </div>
  );
}

export function KBPlaceholder({
  icon: Ico,
  title,
  body,
}: {
  icon: IconComponent;
  title: string;
  body: string;
}): JSX.Element {
  return (
    <div style={{ padding: "56px 24px", textAlign: "center" }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: "var(--c-bg-sunken)",
          color: "var(--c-ink-3)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 14,
        }}
      >
        <Ico size={20} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: "var(--c-ink-3)", maxWidth: 380, margin: "0 auto" }}>
        {body}
      </div>
    </div>
  );
}
